/**
 * The 2D equivalent of the map: every located facility ranked by open work,
 * with the same figures and the same category colours, and a horizontal
 * stacked bar per row on one shared linear scale (the honest way to compare
 * magnitudes). It is the keyboard and screen-reader path to the facilities:
 * pressing a facility name (click, Enter or Space - never mere focus) selects
 * it, exactly like clicking its column on the map. The selected facility's
 * detail is rendered as the row directly after it, so Tab moves from the name
 * straight into its detail and "Show these records". A facility chosen on the
 * map that is not among the listed rows gets its detail above the table.
 *
 * "Total shown" counts only the categories shown in the legend; hidden
 * categories keep their column of numbers, marked hidden.
 */
import React, { useMemo, useState } from 'react';
import { DOMAIN } from '../../../../components/tide/tokens';
import { Marker } from '../../../../components/tide/Marker';
import { ATLAS_DOMAINS, type AtlasDomain, type AtlasFacility } from './atlasApi';
import { shownTotal } from './prismGeometry';

export type AtlasSort = 'total' | AtlasDomain;
const SORTS: Array<{ key: AtlasSort; label: string }> = [
  { key: 'total', label: 'Total shown' },
  ...ATLAS_DOMAINS.map((d) => ({ key: d as AtlasSort, label: DOMAIN[d].label })),
];
const PAGE = 25;
const fmt = (n: number) => n.toLocaleString('en-US');

export function rankFacilities(facilities: AtlasFacility[], shown: AtlasDomain[], sort: AtlasSort): AtlasFacility[] {
  const key = (f: AtlasFacility) => (sort === 'total' ? shownTotal(f.open, shown) : f.open[sort]);
  return facilities
    .filter((f) => shownTotal(f.open, shown) > 0)
    .sort((a, b) => key(b) - key(a) || shownTotal(b.open, shown) - shownTotal(a.open, shown) || a.name.localeCompare(b.name));
}

/** Columns in the table: #, Facility, one per domain, Total shown, Bar, Oldest open. */
const COLS = 5 + ATLAS_DOMAINS.length;

export default function AtlasList({ facilities, shown, selectedId, onSelect, maxTotal, hint, renderDetail }: {
  facilities: AtlasFacility[];
  shown: AtlasDomain[];
  selectedId: string | null;
  onSelect(id: string): void;
  /** The largest all-category total: the bars' fixed scale, as for the columns. */
  maxTotal: number;
  /** One muted line in the list header (e.g. how to open a facility's detail); null hides it. */
  hint?: string | null;
  /** The selected facility's detail panel. */
  renderDetail?(facility: AtlasFacility): React.ReactNode;
}) {
  const [sort, setSort] = useState<AtlasSort>('total');
  const [all, setAll] = useState(false);
  const ranked = useMemo(() => rankFacilities(facilities, shown, sort), [facilities, shown, sort]);
  const rows = all ? ranked : ranked.slice(0, PAGE);
  const selected = selectedId ? facilities.find((f) => f.id === selectedId) || null : null;
  const selectedInRows = !!selected && rows.some((f) => f.id === selected.id);
  const withoutShown = facilities.length - ranked.length;
  const sortLabel = SORTS.find((s) => s.key === sort)?.label || '';

  return (
    <section className="atlas-list" aria-labelledby="atlas-list-title">
      <div className="atlas-list__head">
        <div className="atlas-list__title">
          <h3 id="atlas-list-title" className="atlas-h3">Facilities ranked by open work</h3>
          {hint && <p className="atlas-note" data-testid="atlas-detail-hint">{hint}</p>}
        </div>
        <div role="group" aria-label="Sort facilities by" className="atlas-sort">
          <span className="atlas-sort__label" aria-hidden="true">Sort by</span>
          {SORTS.map((s) => (
            <button
              key={s.key}
              type="button"
              className="atlas-sort__btn"
              aria-pressed={sort === s.key}
              onClick={() => setSort(s.key)}
            >{s.label}{s.key !== 'total' && !shown.includes(s.key as AtlasDomain) ? ' (hidden)' : ''}</button>
          ))}
        </div>
      </div>
      <p className="atlas-note">
        {shown.length === 0
          ? 'No categories selected, so no facility is ranked. Choose a category in the legend or Show all.'
          : `Ranked by ${sortLabel.toLowerCase()}. Total shown counts ${shown.map((d) => DOMAIN[d].label).join(', ')}.`}
        {shown.length > 0 && withoutShown > 0 && ` ${fmt(withoutShown)} ${withoutShown === 1 ? 'facility has' : 'facilities have'} open work only in hidden categories and ${withoutShown === 1 ? 'is' : 'are'} not listed.`}
      </p>

      {selected && !selectedInRows && renderDetail && (
        <div className="atlas-detail-above" data-testid="atlas-detail-above">{renderDetail(selected)}</div>
      )}

      {rows.length > 0 && (
        // A named, focusable region so keyboard users can scroll it sideways on a narrow screen.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        <div className="atlas-table-wrap" tabIndex={0} role="region" aria-label="Facilities ranked by open work, scrollable">
          <table className="atlas-table">
            <caption className="tide-sr">
              Located facilities with open work, ranked by {sortLabel.toLowerCase()}. Showing {rows.length} of {ranked.length}.
            </caption>
            <thead>
              <tr>
                <th scope="col" className="atlas-num">#</th>
                <th scope="col">Facility</th>
                {ATLAS_DOMAINS.map((d) => (
                  <th key={d} scope="col" className="atlas-num" aria-sort={sort === d ? 'descending' : undefined} data-hidden={shown.includes(d) ? undefined : 'true'}>
                    <span className="atlas-th-key"><Marker shape={DOMAIN[d].marker} color={DOMAIN[d].color} size={10} />{DOMAIN[d].label}</span>
                    {!shown.includes(d) && <span className="atlas-hidden-word"> hidden</span>}
                  </th>
                ))}
                <th scope="col" className="atlas-num" aria-sort={sort === 'total' ? 'descending' : undefined}>Total shown</th>
                <th scope="col" className="atlas-bar-col"><span className="tide-sr">Bar, same scale for every row</span></th>
                <th scope="col" className="atlas-num">Oldest open</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((f, i) => {
                const total = shownTotal(f.open, shown);
                const isSelected = f.id === selectedId;
                return (
                  <React.Fragment key={f.id}>
                  <tr data-testid="atlas-row" data-facility={f.id} data-selected={isSelected ? 'true' : undefined}>
                    <td className="atlas-num">{i + 1}</td>
                    <th scope="row" className="atlas-fac">
                      <button
                        type="button"
                        className="atlas-fac__btn"
                        aria-expanded={isSelected}
                        data-row-button={f.id}
                        onClick={() => onSelect(f.id)}
                      >{f.name}</button>
                      <span className="atlas-fac__region">{f.region || 'Region not recorded'}</span>
                    </th>
                    {ATLAS_DOMAINS.map((d) => (
                      <td key={d} className="atlas-num" data-hidden={shown.includes(d) ? undefined : 'true'} data-testid={`cell-${d}`}>{fmt(f.open[d])}</td>
                    ))}
                    <td className="atlas-num atlas-total" data-testid="row-total">{fmt(total)}</td>
                    <td className="atlas-bar-col" aria-hidden="true">
                      <span className="atlas-bar">
                        {ATLAS_DOMAINS.filter((d) => shown.includes(d) && f.open[d] > 0).map((d) => (
                          <span key={d} className="atlas-bar__seg" style={{ width: `${(f.open[d] / Math.max(1, maxTotal)) * 100}%`, background: DOMAIN[d].color }} />
                        ))}
                      </span>
                    </td>
                    <td className="atlas-num">{f.oldestOpenDays == null ? <span className="atlas-na">not recorded</span> : `${f.oldestOpenDays.toFixed(1)} d`}</td>
                  </tr>
                  {isSelected && renderDetail && (
                    <tr className="atlas-detail-row" data-testid="atlas-detail-row">
                      <td colSpan={COLS}>{renderDetail(f)}</td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {ranked.length > PAGE && (
        <p className="atlas-more">
          <span>Showing {fmt(rows.length)} of {fmt(ranked.length)} facilities.</span>
          <button type="button" className="atlas-link-btn" onClick={() => setAll((v) => !v)}>
            {all ? `Show the top ${PAGE}` : `Show all ${fmt(ranked.length)}`}
          </button>
        </p>
      )}
      <p className="atlas-note">Oldest open is the longest time an open record here has been in its current stage, among records whose stage-entry time is recorded.</p>
    </section>
  );
}
